import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Colors, Spacing, FontSizes, BorderRadius, Shadows } from '../../constants/theme';
import { KpiType } from '../../types/drilldown';
import DataExportService from '../../utils/dataExport';
import ModernTable, { Column, CellHelpers } from '../tables/ModernTable';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-out;
  backdrop-filter: blur(4px);
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  width: 90vw;
  max-width: 1200px;
  height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${Shadows.xl};
  animation: slideUp 0.3s ease-out;
  
  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ModalHeader = styled.div`
  padding: ${Spacing.xl}px ${Spacing.xxl}px;
  border-bottom: 1px solid ${Colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, ${Colors.surface} 0%, ${Colors.background} 100%);
`;

const ModalTitle = styled.h2`
  font-size: ${FontSizes.xl}px;
  color: ${Colors.textPrimary};
  margin: 0;
  font-weight: 600;
  letter-spacing: -0.02em;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${Colors.textTertiary};
  font-size: 24px;
  cursor: pointer;
  padding: ${Spacing.sm}px;
  width: 40px;
  height: 40px;
  border-radius: ${BorderRadius.sm}px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.surfaceHover};
    color: ${Colors.textPrimary};
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${Spacing.xxl}px;
  background: ${Colors.background};
`;

const BreadcrumbNav = styled.nav`
  display: flex;
  align-items: center;
  gap: ${Spacing.md}px;
  margin-bottom: ${Spacing.xxl}px;
  padding: ${Spacing.lg}px;
  background: ${Colors.surface};
  border-radius: ${BorderRadius.md}px;
  border: 1px solid ${Colors.border};
`;

const BreadcrumbItem = styled.button<{ active?: boolean }>`
  background: ${props => props.active ? Colors.primary : 'transparent'};
  color: ${props => props.active ? 'white' : Colors.textSecondary};
  border: none;
  padding: ${Spacing.sm}px ${Spacing.lg}px;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${props => props.active ? 600 : 400};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? Colors.primary : Colors.surfaceHover};
    color: ${props => props.active ? 'white' : Colors.textPrimary};
  }
  
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const BreadcrumbSeparator = styled.span`
  color: ${Colors.textTertiary};
  font-size: ${FontSizes.sm}px;
`;

const SummarySection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xxl}px;
`;

const SummaryCard = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.lg}px;
`;

const SummaryLabel = styled.div`
  color: ${Colors.textTertiary};
  font-size: ${FontSizes.xs}px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${Spacing.xs}px;
`;

const SummaryValue = styled.div<{ color?: string }>`
  color: ${props => props.color || Colors.textPrimary};
  font-size: ${FontSizes.xxl}px;
  font-weight: 700;
  line-height: 1.2;
`;

const SummaryChange = styled.div<{ positive?: boolean }>`
  color: ${props => props.positive ? Colors.success : Colors.danger};
  font-size: ${FontSizes.sm}px;
  margin-top: ${Spacing.xs}px;
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
`;

const DataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${Spacing.lg}px;
  margin-bottom: ${Spacing.xxl}px;
`;

const DataCard = styled.div<{ clickable?: boolean }>`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.xl}px;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: all 0.2s ease;
  position: relative;
  
  ${props => props.clickable && `
    &:hover {
      background: ${Colors.surfaceHover};
      border-color: ${Colors.primary};
      transform: translateY(-2px);
      box-shadow: ${Shadows.md};
    }
    
    &::after {
      content: '→';
      position: absolute;
      right: ${Spacing.lg}px;
      top: 50%;
      transform: translateY(-50%);
      color: ${Colors.primary};
      font-size: ${FontSizes.lg}px;
      opacity: 0;
      transition: opacity 0.2s ease;
    }
    
    &:hover::after {
      opacity: 1;
    }
  `}
`;

const DataCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.md}px;
`;

const DataCardTitle = styled.div`
  color: ${Colors.textPrimary};
  font-size: ${FontSizes.md}px;
  font-weight: 600;
  line-height: 1.3;
`;

const DataCardBadge = styled.div<{ type?: 'success' | 'warning' | 'danger' | 'info' }>`
  background: ${props => {
    switch(props.type) {
      case 'success': return Colors.success + '20';
      case 'warning': return Colors.warning + '20';
      case 'danger': return Colors.danger + '20';
      case 'info': return Colors.info + '20';
      default: return Colors.surfaceHover;
    }
  }};
  color: ${props => {
    switch(props.type) {
      case 'success': return Colors.success;
      case 'warning': return Colors.warning;
      case 'danger': return Colors.danger;
      case 'info': return Colors.info;
      default: return Colors.textSecondary;
    }
  }};
  padding: ${Spacing.xs}px ${Spacing.sm}px;
  border-radius: ${BorderRadius.xs}px;
  font-size: ${FontSizes.xs}px;
  font-weight: 600;
`;

const DataCardMetric = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs}px;
`;

const MetricValue = styled.div`
  color: ${Colors.textPrimary};
  font-size: ${FontSizes.xxl}px;
  font-weight: 700;
`;

const MetricLabel = styled.div`
  color: ${Colors.textSecondary};
  font-size: ${FontSizes.sm}px;
`;

const MetricSubtext = styled.div`
  color: ${Colors.textTertiary};
  font-size: ${FontSizes.xs}px;
  margin-top: ${Spacing.xs}px;
`;


const ActionBar = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
  margin-bottom: ${Spacing.xl}px;
`;

const ActionButton = styled.button`
  background: ${Colors.surface};
  color: ${Colors.textPrimary};
  border: 1px solid ${Colors.border};
  padding: ${Spacing.sm}px ${Spacing.lg}px;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${Colors.primary};
    color: white;
    border-color: ${Colors.primary};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${Spacing.xxxl}px;
  color: ${Colors.textSecondary};
`;

interface CleanDrillDownModalProps {
  kpiType: KpiType;
  initialValue: number | string;
  onClose: () => void;
}

export const CleanDrillDownModal: React.FC<CleanDrillDownModalProps> = ({
  kpiType,
  initialValue,
  onClose
}) => {
  const [currentLevel, setCurrentLevel] = useState(0);
  const [drilldownData, setDrilldownData] = useState<any>(null);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

  useEffect(() => {
    loadDrilldownData();
  }, [currentLevel, selectedFilter, kpiType]);

  const loadDrilldownData = () => {
    const data = generateDrilldownData(kpiType, currentLevel, selectedFilter);
    setDrilldownData(data);
  };

  const generateDrilldownData = (kpi: KpiType, level: number, filter: string | null) => {
    const kpiConfigs = {
      totalEmployees: {
        title: 'Total Employees',
        levels: ['Overview', 'Department', 'Country/Region', 'Job Level', 'Individuals'],
        data: generateEmployeesData(level, filter)
      },
      attritionRisk: {
        title: 'Attrition Risk',
        levels: ['Overview', 'Risk Category', 'Department', 'Country', 'Employee List'],
        data: generateAttritionData(level, filter)
      },
      engagementScore: {
        title: 'Engagement Score',
        levels: ['Overview', 'Department', 'Country', 'Tenure', 'Survey Questions'],
        data: generateEngagementData(level, filter)
      },
      leadershipReady: {
        title: 'Leadership Ready',
        levels: ['Overview', 'Role Criticality', 'Department', 'Country', 'Role List'],
        data: generateLeadershipData(level, filter)
      },
      timeToFill: {
        title: 'Time to Fill',
        levels: ['Overview', 'Role Type', 'Department', 'Country', 'Requisitions'],
        data: generateTimeToFillData(level, filter)
      },
      diversityScore: {
        title: 'Diversity Score',
        levels: ['Overview', 'DEI Category', 'Department', 'Country', 'Metrics'],
        data: generateDiversityData(level, filter)
      }
    };

    return kpiConfigs[kpi] || kpiConfigs.totalEmployees;
  };

  const generateEmployeesData = (level: number, filter: string | null) => {
    const data: any = {
      summary: {
        total: 1847,
        change: '+2.3%',
        target: 2000,
        utilization: '92.4%'
      }
    };

    switch(level) {
      case 0: // Overview
        data.items = [
          { name: 'Engineering', value: 450, percentage: 24.4, trend: '+5%', badge: 'Largest' },
          { name: 'Sales', value: 280, percentage: 15.2, trend: '+8%' },
          { name: 'Operations', value: 320, percentage: 17.3, trend: '+3%' },
          { name: 'Marketing', value: 180, percentage: 9.7, trend: '+12%' },
          { name: 'HR', value: 120, percentage: 6.5, trend: '-2%' },
          { name: 'Finance', value: 150, percentage: 8.1, trend: '+1%' }
        ];
        break;
      case 1: // Department details
        data.items = [
          { name: 'United States', value: 680, percentage: 36.8, trend: '+4%' },
          { name: 'United Kingdom', value: 320, percentage: 17.3, trend: '+2%' },
          { name: 'Germany', value: 250, percentage: 13.5, trend: '+6%' },
          { name: 'France', value: 180, percentage: 9.7, trend: '-1%' }
        ];
        break;
      case 2: // Country/Region
        data.items = [
          { name: 'L1-L3 (Junior)', value: 450, percentage: 30, trend: '+8%' },
          { name: 'L4-L6 (Mid)', value: 600, percentage: 40, trend: '+3%' },
          { name: 'L7-L9 (Senior)', value: 350, percentage: 23, trend: '-2%' },
          { name: 'L10+ (Executive)', value: 100, percentage: 7, trend: '0%' }
        ];
        break;
      case 3: // Job Level
      case 4: // Individuals
        data.isTable = true;
        data.headers = ['Name', 'Department', 'Level', 'Location', 'Tenure', 'Performance'];
        data.rows = [
          ['John Smith', 'Engineering', 'L5', 'US - California', '3.2 years', 'Exceeds'],
          ['Sarah Johnson', 'Sales', 'L7', 'UK - London', '5.1 years', 'Meets'],
          ['Michael Chen', 'Engineering', 'L6', 'US - New York', '2.8 years', 'Exceeds'],
          ['Emily Davis', 'Marketing', 'L4', 'Germany - Berlin', '1.5 years', 'Meets'],
          ['Robert Wilson', 'Finance', 'L8', 'US - Texas', '7.3 years', 'Exceeds']
        ];
        break;
    }

    return data;
  };

  const generateAttritionData = (level: number, filter: string | null) => {
    const data: any = {
      summary: {
        total: '11.4%',
        change: '-1.2pp',
        target: '< 10%',
        atRisk: 225
      }
    };

    switch(level) {
      case 0: // Overview
        data.items = [
          { name: 'High Risk', value: '15%', count: 225, trend: '+2%', badge: 'Critical' },
          { name: 'Medium Risk', value: '25%', count: 375, trend: '-1%', badge: 'Warning' },
          { name: 'Low Risk', value: '60%', count: 900, trend: '-3%', badge: 'Stable' }
        ];
        break;
      case 1: // Risk Category
        data.items = [
          { name: 'Engineering', value: '18%', count: 81, trend: '+3%', badge: 'High' },
          { name: 'Sales', value: '22%', count: 62, trend: '+5%', badge: 'Critical' },
          { name: 'HR', value: '8%', count: 10, trend: '-2%', badge: 'Low' },
          { name: 'Marketing', value: '15%', count: 27, trend: '+1%', badge: 'Medium' }
        ];
        break;
      case 2: // Department
      case 3: // Country
      case 4: // Employee List
        data.isTable = true;
        data.headers = ['Name', 'Department', 'Risk Score', 'Factors', 'Last Engagement', 'Action'];
        data.rows = [
          ['Alice Brown', 'Sales', 'High (85%)', 'Comp Gap, No Promo', '3.2/10', 'Urgent'],
          ['David Lee', 'Engineering', 'High (78%)', 'Manager Change', '4.1/10', 'Review'],
          ['Lisa Wang', 'Marketing', 'Medium (52%)', 'Career Growth', '5.8/10', 'Monitor'],
          ['James Miller', 'Finance', 'Low (22%)', 'None', '7.9/10', 'Stable']
        ];
        break;
    }

    return data;
  };

  const generateEngagementData = (level: number, filter: string | null) => {
    const data: any = {
      summary: {
        total: '8.1/10',
        change: '+0.4',
        target: '8.5/10',
        participation: '92%'
      }
    };

    switch(level) {
      case 0:
      case 1:
        data.items = [
          { name: 'Engineering', value: '8.2', trend: '+0.3', badge: 'Good' },
          { name: 'Sales', value: '7.8', trend: '+0.1', badge: 'Fair' },
          { name: 'HR', value: '8.9', trend: '+0.5', badge: 'Excellent' },
          { name: 'Marketing', value: '8.1', trend: '+0.2', badge: 'Good' }
        ];
        break;
      case 2:
      case 3:
      case 4:
        data.isTable = true;
        data.headers = ['Question Category', 'Score', 'Change', 'Responses', 'Sentiment'];
        data.rows = [
          ['Leadership Trust', '8.3/10', '+0.4', '1,702', 'Positive'],
          ['Career Growth', '7.5/10', '+0.2', '1,698', 'Neutral'],
          ['Work-Life Balance', '7.2/10', '-0.3', '1,705', 'Mixed'],
          ['Compensation', '7.0/10', '+0.1', '1,690', 'Neutral'],
          ['Team Culture', '8.5/10', '+0.5', '1,708', 'Positive']
        ];
        break;
    }

    return data;
  };

  const generateLeadershipData = (level: number, filter: string | null) => {
    const data: any = {
      summary: {
        total: '73%',
        change: '+5.2pp',
        target: '80%',
        criticalRoles: 45
      }
    };

    switch(level) {
      case 0:
      case 1:
        data.items = [
          { name: 'High Criticality', value: '72%', count: 32, trend: '+3%', badge: 'Priority' },
          { name: 'Medium Criticality', value: '85%', count: 102, trend: '+7%', badge: 'Good' },
          { name: 'Low Criticality', value: '92%', count: 74, trend: '+2%', badge: 'Excellent' }
        ];
        break;
      default:
        data.isTable = true;
        data.headers = ['Role', 'Incumbent', 'Successors', 'Readiness', 'Timeline'];
        data.rows = [
          ['VP Engineering', 'John Davis', '2 identified', 'Ready Now', 'Q1 2025'],
          ['Director Sales', 'Sarah Wilson', '1 identified', 'Ready Now', 'Q2 2025'],
          ['CFO', 'Robert Taylor', '1 developing', '12-18 months', 'Q4 2025'],
          ['Head of HR', 'Emily Brown', '2 identified', 'Ready Now', 'Q1 2025']
        ];
        break;
    }

    return data;
  };

  const generateTimeToFillData = (level: number, filter: string | null) => {
    const data: any = {
      summary: {
        total: '38 days',
        change: '-4 days',
        target: '30 days',
        openRoles: 127
      }
    };

    switch(level) {
      case 0:
      case 1:
        data.items = [
          { name: 'Technical', value: '48 days', count: 45, trend: '-5 days', badge: 'Slow' },
          { name: 'Sales', value: '35 days', count: 32, trend: '-8 days', badge: 'Good' },
          { name: 'Operations', value: '28 days', count: 28, trend: '-4 days', badge: 'Fast' },
          { name: 'Leadership', value: '75 days', count: 8, trend: '+3 days', badge: 'Critical' }
        ];
        break;
      default:
        data.isTable = true;
        data.headers = ['Position', 'Department', 'Posted', 'Days Open', 'Candidates', 'Status'];
        data.rows = [
          ['Senior Engineer', 'Engineering', '2024-01-15', '46', '127', 'Interview'],
          ['Sales Manager', 'Sales', '2024-02-01', '35', '89', 'Final Round'],
          ['Product Designer', 'Design', '2024-01-20', '54', '203', 'Screening'],
          ['Data Analyst', 'Analytics', '2024-02-05', '28', '156', 'Offer']
        ];
        break;
    }

    return data;
  };

  const generateDiversityData = (level: number, filter: string | null) => {
    const data: any = {
      summary: {
        total: '74%',
        change: '+1.8pp',
        target: '85%',
        representation: 1365
      }
    };

    switch(level) {
      case 0:
      case 1:
        data.items = [
          { name: 'Gender Diversity', value: '68%', target: '50%', trend: '+3%', badge: 'Exceeding' },
          { name: 'Ethnic Diversity', value: '42%', target: '40%', trend: '+2%', badge: 'On Track' },
          { name: 'Age Diversity', value: '78%', target: '75%', trend: '+1%', badge: 'Exceeding' },
          { name: 'Disability Inclusion', value: '8%', target: '10%', trend: '+1%', badge: 'Below' }
        ];
        break;
      default:
        data.isTable = true;
        data.headers = ['Metric', 'Current', 'Target', 'Gap', 'Trend', 'Actions'];
        data.rows = [
          ['Female Leadership', '38%', '40%', '-2pp', '+5%', 'Mentorship Program'],
          ['Ethnic Minorities', '42%', '40%', '+2pp', '+8%', 'Maintain'],
          ['Age 50+', '22%', '20%', '+2pp', '+2%', 'Succession Planning'],
          ['LGBTQ+', '6%', '5%', '+1pp', '+2%', 'Inclusive Policies']
        ];
        break;
    }

    return data;
  };

  const handleDrillDown = (itemName: string) => {
    if (currentLevel < 4) {
      setSelectedFilter(itemName);
      setCurrentLevel(currentLevel + 1);
    }
  };

  const handleBreadcrumbClick = (level: number) => {
    setCurrentLevel(level);
    if (level === 0) {
      setSelectedFilter(null);
    }
  };

  const handleExport = () => {
    if (drilldownData) {
      DataExportService.exportDrilldownData(drilldownData, kpiType);
    }
  };

  const getBreadcrumbLabels = () => {
    if (!drilldownData) return [];
    return drilldownData.levels?.slice(0, currentLevel + 1) || ['Overview'];
  };

  const formatKpiTitle = (kpi: KpiType): string => {
    const titles = {
      totalEmployees: 'Total Employees',
      attritionRisk: 'Attrition Risk',
      engagementScore: 'Engagement Score',
      leadershipReady: 'Leadership Ready',
      timeToFill: 'Time to Fill',
      diversityScore: 'Diversity Score'
    };
    return titles[kpi] || kpi;
  };

  const getBadgeType = (badge: string): 'success' | 'warning' | 'danger' | 'info' => {
    const badgeMap: { [key: string]: 'success' | 'warning' | 'danger' | 'info' } = {
      'Excellent': 'success',
      'Good': 'success',
      'Exceeding': 'success',
      'Fast': 'success',
      'Stable': 'success',
      'On Track': 'info',
      'Fair': 'info',
      'Medium': 'warning',
      'Warning': 'warning',
      'Slow': 'warning',
      'Below': 'warning',
      'Critical': 'danger',
      'High': 'danger',
      'Priority': 'danger'
    };
    return badgeMap[badge] || 'info';
  };

  const getTableColumns = (): Column[] => {
    if (!drilldownData?.data?.headers) return [];
    
    return drilldownData.data.headers.map((header: string, index: number) => ({
      key: `col_${index}`,
      header,
      sortable: true,
      render: (value: any, row: any, rowIndex: number) => {
        const cellValue = drilldownData.data.rows[rowIndex]?.[index] || value;
        
        // Special rendering for badge columns
        if (header.toLowerCase().includes('status') || 
            header.toLowerCase().includes('level') ||
            header.toLowerCase().includes('risk')) {
          return <CellHelpers.Badge value={cellValue} variant={getBadgeType(cellValue)} />;
        }
        
        // Special rendering for percentage columns
        if (header.toLowerCase().includes('score') || 
            header.toLowerCase().includes('percent') ||
            cellValue?.toString().includes('%')) {
          const numValue = parseFloat(cellValue?.toString().replace('%', '') || '0');
          return <CellHelpers.Percentage value={numValue} />;
        }
        
        // Special rendering for employee names (first column usually)
        if (index === 0 && header.toLowerCase().includes('employee')) {
          return <CellHelpers.Avatar name={cellValue} />;
        }
        
        return cellValue;
      }
    }));
  };

  const getTableData = () => {
    if (!drilldownData?.data?.rows) return [];
    
    return drilldownData.data.rows.map((row: string[], index: number) => {
      const rowData: any = { id: index };
      drilldownData.data.headers.forEach((header: string, colIndex: number) => {
        rowData[`col_${colIndex}`] = row[colIndex];
      });
      return rowData;
    });
  };

  if (!drilldownData) {
    return (
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <EmptyState>Loading...</EmptyState>
        </ModalContent>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{formatKpiTitle(kpiType)} - Analysis</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        
        <ModalBody>
          <ActionBar>
            <ActionButton onClick={handleExport}>Export Data</ActionButton>
            <ActionButton onClick={() => window.print()}>Print Report</ActionButton>
          </ActionBar>

          <BreadcrumbNav>
            {getBreadcrumbLabels().map((label: string, index: number) => (
              <React.Fragment key={index}>
                {index > 0 && <BreadcrumbSeparator>/</BreadcrumbSeparator>}
                <BreadcrumbItem
                  active={index === currentLevel}
                  onClick={() => handleBreadcrumbClick(index)}
                  disabled={index > currentLevel}
                >
                  {label}
                </BreadcrumbItem>
              </React.Fragment>
            ))}
          </BreadcrumbNav>

          {drilldownData.data && (
            <>
              <SummarySection>
                <SummaryCard>
                  <SummaryLabel>Current Value</SummaryLabel>
                  <SummaryValue>{drilldownData.data.summary?.total || initialValue}</SummaryValue>
                  {drilldownData.data.summary?.change && (
                    <SummaryChange positive={drilldownData.data.summary.change.startsWith('+')}>
                      <span>{drilldownData.data.summary.change}</span>
                      <span>vs last period</span>
                    </SummaryChange>
                  )}
                </SummaryCard>
                
                {drilldownData.data.summary?.target && (
                  <SummaryCard>
                    <SummaryLabel>Target</SummaryLabel>
                    <SummaryValue>{drilldownData.data.summary.target}</SummaryValue>
                  </SummaryCard>
                )}
                
                {Object.entries(drilldownData.data.summary || {}).filter(([key]) => 
                  !['total', 'change', 'target'].includes(key)
                ).map(([key, value]: [string, any]) => (
                  <SummaryCard key={key}>
                    <SummaryLabel>{key.charAt(0).toUpperCase() + key.slice(1)}</SummaryLabel>
                    <SummaryValue>{value}</SummaryValue>
                  </SummaryCard>
                ))}
              </SummarySection>

              {drilldownData.data.isTable ? (
                <ModernTable
                  columns={getTableColumns()}
                  data={getTableData()}
                  keyField="id"
                  showPagination={true}
                  pageSize={10}
                  currentPage={1}
                  onPageChange={(page) => console.log('Page:', page)}
                  emptyMessage="No data available for this analysis"
                />
              ) : (
                <DataGrid>
                  {drilldownData.data.items?.map((item: any, index: number) => (
                    <DataCard
                      key={index}
                      clickable={currentLevel < 3}
                      onClick={() => currentLevel < 3 && handleDrillDown(item.name)}
                    >
                      <DataCardHeader>
                        <DataCardTitle>{item.name}</DataCardTitle>
                        {item.badge && (
                          <DataCardBadge type={getBadgeType(item.badge)}>
                            {item.badge}
                          </DataCardBadge>
                        )}
                      </DataCardHeader>
                      
                      <DataCardMetric>
                        <MetricValue>{item.value}</MetricValue>
                        {item.percentage && (
                          <MetricLabel>{item.percentage}% of total</MetricLabel>
                        )}
                        {item.count && (
                          <MetricSubtext>{item.count} items</MetricSubtext>
                        )}
                        {item.trend && (
                          <MetricSubtext>Trend: {item.trend}</MetricSubtext>
                        )}
                        {item.target && (
                          <MetricSubtext>Target: {item.target}</MetricSubtext>
                        )}
                      </DataCardMetric>
                    </DataCard>
                  ))}
                </DataGrid>
              )}
            </>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CleanDrillDownModal;